import { AmostrasDeFormulario } from './amostras/Formularios';
import { ComponentShowcase } from './ComponentShowcase';
import { FormSection } from '@/ui/FormSection';
import { FormFieldWrapper } from '@/ui/FormField';
import { FormBackButton } from '@/ui/FormBackButton';
import { Input } from '@/ui/base/input';
import { Button } from '@/ui/base/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { User, Car, Save } from 'lucide-react';
import { toast } from 'sonner';

export function FormulariosTab() {
  return (
    <div className="space-y-6">
      {/* FormPage */}
      <ComponentShowcase
        id="form-page"
        title="FormPage"
        description="Moldura padrão de formulário de criação e edição: voltar, o formulário e o rodapé (Cancelar e Salvar), centralizados com largura máxima."
        code={`<FormPage
  onSubmit={handleSubmit}
  isSaving={isPending}
  submitLabel="Cadastrar"
  backTo="/credenciados"
  headerContent={<AvisoDeRascunho aoRestaurar={...} aoDescartar={...} />}
>
  <FormSection title="Dados Gerais" icon={User}>
    {/* campos */}
  </FormSection>
</FormPage>`}
        propsTable={[
          ['onSubmit', '(e: FormEvent) => void', '-', 'Chamado ao enviar o formulário'],
          ['isSaving', 'boolean', 'false', "Desabilita o envio e mostra 'Salvando...'"],
          ['submitLabel', 'string', '"Salvar"', 'Rótulo do botão de enviar'],
          ['submitIcon', 'ReactNode', '<Save />', 'Ícone do botão de enviar'],
          ['submitDisabled', 'boolean', 'false', 'Desabilita o envio além de isSaving'],
          ['maxWidth', 'string', '"max-w-3xl"', 'Largura máxima do formulário'],
          ['headerContent', 'ReactNode', '-', 'Conteúdo acima do form (ex: AvisoDeRascunho)'],
          ['backTo', 'string', '-', 'Caminho de retorno (default: navigate(-1))'],
          ['hideBack', 'boolean', 'false', 'Oculta o FormBackButton'],
          ['hideFooter', 'boolean', 'false', 'Oculta o rodapé (Cancelar e enviar)'],
          ['onCancel', '() => void', '-', 'Ação própria para Cancelar'],
        ]}
      >
        {/* Amostra do formulário */}
        <div className="w-full max-w-3xl mx-auto space-y-4">
          <FormBackButton label="Voltar" />
          <form
            onSubmit={(e) => {
              e.preventDefault();
              toast('Nada foi enviado', {
                description: 'Na tela real, Salvar chama a API. Esta amostra não grava.',
              });
            }}
            className="space-y-4"
          >
            <FormSection title="Dados do Credenciado" icon={User}>
              <div className="grid sm:grid-cols-2 gap-4">
                <FormFieldWrapper label="Nome" required>
                  <Input placeholder="Pessoa Exemplo Um" />
                </FormFieldWrapper>
                <FormFieldWrapper label="CPF/CNPJ" required>
                  <Input placeholder="000.000.000-00" />
                </FormFieldWrapper>
              </div>
            </FormSection>
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
              <Button type="button" variant="outline" className="rounded-xl">
                Cancelar
              </Button>
              <Button type="submit" className="rounded-xl">
                <Save className="h-4 w-4 mr-2" /> Salvar
              </Button>
            </div>
          </form>
        </div>
      </ComponentShowcase>

      {/* FormSection */}
      <ComponentShowcase
        id="form-section"
        title="FormSection"
        description="Cartão que agrupa campos de formulário: ícone com fundo sutil, título em caixa alta e subtítulo opcional."
        code={`<FormSection title="Dados do Veículo" subtitle="Informações da frota" icon={Car}>
  <Input placeholder="Placa" />
  <Input placeholder="Modelo" />
</FormSection>`}
        propsTable={[
          ['title', 'string', '-', 'Título em caixa alta (tracking-wider)'],
          ['subtitle', 'string', '-', 'Subtítulo descritivo'],
          ['icon', 'LucideIcon', '-', 'Ícone com fundo primary/8'],
          ['className', 'string', '-', 'Classes a mais no cartão'],
        ]}
      >
        <div className="grid sm:grid-cols-2 gap-4 w-full">
          <FormSection title="Dados Pessoais" icon={User}>
            <Input placeholder="Nome completo" />
            <Input placeholder="E-mail" />
          </FormSection>
          <FormSection title="Dados do Veículo" subtitle="Frota ativa" icon={Car}>
            <Input placeholder="Placa — ABC-1D23" />
            <Input placeholder="Modelo" />
          </FormSection>
        </div>
      </ComponentShowcase>

      {/* FormFieldWrapper */}
      <ComponentShowcase
        id="form-field-wrapper"
        title="FormFieldWrapper"
        description="Moldura de campo: rótulo em caixa alta, asterisco de obrigatório, texto de apoio e mensagem de erro. Com erro, o rótulo, o asterisco e a mensagem usam --destructive-text, o token feito para texto, que a aba Cores mede sobre o cartão e sobre o fundo nos quatro temas."
        code={`<FormFieldWrapper label="Nome" required error="Campo obrigatório" hint="Nome completo">
  <Input placeholder="Pessoa Exemplo Um" />
</FormFieldWrapper>`}
        propsTable={[
          ['label', 'string', '-', 'Rótulo em caixa alta'],
          ['required', 'boolean', 'false', 'Exibe asterisco vermelho'],
          ['error', 'string', '-', 'Mensagem de erro (substitui o texto de apoio)'],
          ['hint', 'string', '-', 'Texto de apoio abaixo do campo'],
          ['htmlFor', 'string', '-', 'ID do campo para acessibilidade'],
          ['trailing', 'ReactNode', '-', 'Conteúdo à direita do rótulo'],
        ]}
      >
        <div className="grid sm:grid-cols-2 gap-4 w-full">
          <FormFieldWrapper label="Nome" required hint="Nome completo do credenciado">
            <Input placeholder="Pessoa Exemplo Um" />
          </FormFieldWrapper>
          <FormFieldWrapper label="E-mail" required hint="Para avisos do credenciamento">
            <Input placeholder="pessoa@exemplo.invalid" />
          </FormFieldWrapper>
          <FormFieldWrapper
            label="CPF"
            htmlFor="amostra-cpf-com-erro"
            required
            error="Informe o CPF com 11 números"
          >
            <Input id="amostra-cpf-com-erro" placeholder="000.000.000-00" aria-invalid />
          </FormFieldWrapper>
          <FormFieldWrapper
            label="DRE"
            trailing={<span className="text-[10px] text-muted-foreground">Opcional</span>}
          >
            <Select>
              <SelectTrigger aria-label="DRE">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cl">Campo Limpo</SelectItem>
                <SelectItem value="bt">Butantã</SelectItem>
              </SelectContent>
            </Select>
          </FormFieldWrapper>
        </div>
      </ComponentShowcase>

      {/* FormBackButton */}
      <ComponentShowcase
        id="form-back-button"
        title="FormBackButton"
        description="Botão fantasma com seta para voltar do formulário: vai para a rota informada ou para a página anterior."
        code={`<FormBackButton />
<FormBackButton to="/credenciados" label="Credenciados" />`}
        propsTable={[
          ['to', 'string', '-', 'Caminho de retorno (default: navigate(-1))'],
          ['label', 'string', '"Voltar"', 'Texto do botão'],
        ]}
      >
        <div className="flex items-center gap-4">
          <FormBackButton label="Voltar" />
          <FormBackButton label="Credenciados" />
        </div>
      </ComponentShowcase>

      <AmostrasDeFormulario />
    </div>
  );
}
